import { HttpEventType } from "@angular/common/http";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { ToastService } from "@core/components/toasts/toasts.service";
import { FileContentModel } from "services/models/fileContent";

const TIMEOUT_CANCEL_DELETE_FILE = 6 * 1000; // 6 seconds

interface FileUpload {
  file: File;
  progress: number;
  inProgress: boolean;
  response?: {
    fileKey: string;
    fileSize: number;
    hashChecksumSHA256: string;
  };
}

@Component({
  selector: "app-dropzone-files",
  styleUrls: ["./dropzone-files.component.scss"],
  templateUrl: "./dropzone-files.component.html",
})
export class AppDropzoneFilesComponent implements OnInit {
  public files: FileUpload[] = [];
  public isDragOver = false;
  public uploadQueue: FileUpload[] = [];
  public activeUploads = 0;
  public fileDeleteIndex: number;
  public fileDeleteTimeout: any;
  public maxConcurrentUploads = 5;

  @Input() multiple = true;
  @Input() uploadFn?: (file: File) => any;
  @Input() beforeUpload?: (file: File) => Promise<any> | any;
  @Input() allowedFileTypes = ["application/pdf"];
  @Input() readonly: boolean = false;
  @Input() fileContent: FileContentModel = null;

  @Output() onChangeFiles = new EventEmitter();
  @Output() onGetFile = new EventEmitter();

  @ViewChild("fileInput") fileInput!: ElementRef;

  constructor(private toasterService: ToastService) { }

  ngOnInit() { }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();

    this.isDragOver = true;
  }

  public onDragLeave(): void {
    this.isDragOver = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();

    this.isDragOver = false;

    if (!event.dataTransfer?.files) {
      return;
    }

    this.handleUploadFiles(event.dataTransfer?.files);
  }

  public handleFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files) {
      return;
    }

    this.handleUploadFiles(input.files);
  }

  public handleRemoveFile(index: number): void {
    this.fileDeleteIndex = index;

    if (this.fileDeleteTimeout) {
      clearTimeout(this.fileDeleteTimeout);
    }

    this.fileDeleteTimeout = setTimeout(() => {
      this.fileDeleteIndex = undefined;
    }, TIMEOUT_CANCEL_DELETE_FILE);
  }

  public handleConfirmRemoveAmount() {
    this.files.splice(this.fileDeleteIndex, 1);

    this.onChangeFiles.emit({ files: this.files });
  }

  public getFile() {
    if (this.fileContent.fileKey) {
      this.onGetFile.emit();
    } 
  }

  public removeFileByIndex(index: number): void {
    if (index >= 0 && index < this.files.length) {
      this.files.splice(index, 1);
    }
  }

  public getFileTypesDescription(): string {
    if (!this.allowedFileTypes.length) return "";

    return this.allowedFileTypes
      .map((type) => {
        const parts = type.split("/");

        return parts.length > 1 ? parts[1].toUpperCase() : type.toUpperCase();
      })
      .join(", ");
  }

  private handleUploadFiles(files: FileList) {
    if (!this.multiple) {
      const file = files[0];
      if (!file) return;

      if (!this.isValidFile(file)) {
        this.toasterService.showWarning(
          "Arquivo inválido. Só é permitido PDF."
        );
        return;
      }

      this.files = [];
      this.uploadQueue = [];

      const fileUpload = { file, progress: 0, inProgress: false } as FileUpload;

      this.files.push(fileUpload);
      this.uploadQueue.push(fileUpload);
    } else {
      Array.from(files).forEach((file) => {
        if (!this.isValidFile(file)) {
          this.toasterService.showWarning(
            "Arquivo inválido. Só é permitido PDF."
          );
          return;
        }

        const fileUpload = {
          file,
          progress: 0,
          inProgress: false,
        } as FileUpload;

        this.files.push(fileUpload);
        this.uploadQueue.push(fileUpload);
      });
    }

    this.fileInput.nativeElement.value = "";

    this.handleStartUpload();
  }

  private handleStartUpload() {
    while (
      this.activeUploads < this.maxConcurrentUploads &&
      this.uploadQueue.length
    ) {
      const fileUpload = this.uploadQueue.shift();

      if (fileUpload) {
        this.uploadFile(fileUpload);
      }
    }
  }

  private async uploadFile(fileUpload: FileUpload) {
    this.activeUploads--;
    this.handleStartUpload();
    this.onChangeFiles.emit({ files: this.files });

    return;

    fileUpload.inProgress = true;

    this.activeUploads++;

    let uploadObservable;

    try {
      let fileToUpload = fileUpload.file;
      if (this.beforeUpload) {
        const result = await this.beforeUpload(fileToUpload);
        fileToUpload = result || fileToUpload;
      }
      uploadObservable = this.uploadFn(fileToUpload);
    } catch (e) {
      this.toasterService.showError("Erro ao preparar arquivo para upload.");
      fileUpload.inProgress = false;
      this.activeUploads--;
      return;
    }

    uploadObservable.subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          fileUpload.progress = Math.round(
            (event.loaded / (event.total || 1)) * 100
          );
        } else if (event.type === HttpEventType.Response) {
          fileUpload.progress = 100;
          fileUpload.response = event.body;
        }
      },
      error: () => {
        this.toasterService.showError(
          `Erro ao enviar o arquivo: ${fileUpload.file.name}`
        );
        fileUpload.inProgress = false;
      },
      complete: () => {
        fileUpload.inProgress = false;
        this.activeUploads--;
        this.handleStartUpload();
        this.onChangeFiles.emit({ files: this.files });
      },
    });
  }

  private isValidFile(file: File) {
    if (this.allowedFileTypes.indexOf(file?.type) === -1) {
      return false;
    }

    return true;
  }

  public get fileName(): string {
    return this.fileContent && this.fileContent.fileName
      ? this.fileContent.fileName
      : "Não há arquivo disponível para visualização.";
  }
}
